import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend,
} from "recharts";
import { api } from "@/lib/api";
import { formatInr, compactInr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
    IndianRupee,
    TrendingUp,
    Ticket,
    AlertCircle,
    MapPin,
    Layers,
} from "lucide-react";
import { CHART_COLORS, TX_TYPES, monthNameOf } from "./shared";

const nowY = new Date().getFullYear();
const nowM = new Date().getMonth() + 1;

export default function RevenueHomePage() {
    const [y, setY] = useState(nowY);
    const [m, setM] = useState(nowM);
    const [monthly, setMonthly] = useState(null);
    const [summary, setSummary] = useState(null);
    const [activeSubs, setActiveSubs] = useState(null);
    const [pending, setPending] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const [mo, su, subs, txp] = await Promise.all([
                    api.get(`/admin/reports/monthly-revenue?year=${y}&month=${m}`),
                    api.get(`/admin/reports/revenue-summary?months=6`),
                    api.get(`/admin/subscriptions?status=ACTIVE&limit=500`),
                    api.get(`/admin/transactions?status=PENDING&limit=500`),
                ]);
                if (cancelled) return;
                setMonthly(mo.data);
                setSummary(su.data);
                setActiveSubs(subs.data);
                setPending(txp.data);
            } catch (e) {
                toast.error("Could not load revenue overview");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [y, m]);

    const topDistrict = monthly?.by_district?.[0];
    const topPackage = monthly?.by_package?.[0];

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="revenue-home-page">
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                        Revenue Command Centre
                    </div>
                    <h1 className="text-3xl font-extrabold text-neutral-900">
                        Overview — {monthNameOf(y, m)}
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Live rollup of subscriptions, top-ups and commissions across Kerala.
                    </p>
                </div>
                <div className="flex items-center gap-2" data-testid="revenue-month-picker">
                    <Select value={String(y)} onValueChange={(v) => setY(Number(v))}>
                        <SelectTrigger className="w-24" data-testid="revenue-year">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {[nowY, nowY - 1, nowY - 2].map((yr) => (
                                <SelectItem key={yr} value={String(yr)}>
                                    {yr}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={String(m)} onValueChange={(v) => setM(Number(v))}>
                        <SelectTrigger className="w-32" data-testid="revenue-month">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((mm) => (
                                <SelectItem key={mm} value={String(mm)}>
                                    {new Date(2000, mm - 1, 1).toLocaleString("en-IN", { month: "long" })}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </header>

            <section className="grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="revenue-kpis">
                <Kpi
                    label="Revenue this month"
                    icon={<IndianRupee size={18} />}
                    value={loading ? null : formatInr(monthly?.total_revenue_paise)}
                    tone="orange"
                    testid="kpi-total"
                />
                <Kpi
                    label="Active subscriptions"
                    icon={<TrendingUp size={18} />}
                    value={loading ? null : String(activeSubs?.total ?? activeSubs?.subscriptions?.length ?? 0)}
                    tone="emerald"
                    testid="kpi-active-subs"
                />
                <Kpi
                    label="Coupon top-ups"
                    icon={<Ticket size={18} />}
                    value={loading ? null : formatInr(monthly?.by_type?.COUPON_TOPUP)}
                    tone="amber"
                    testid="kpi-topups"
                />
                <Kpi
                    label="Pending payments"
                    icon={<AlertCircle size={18} />}
                    value={loading ? null : String(pending?.total ?? pending?.transactions?.length ?? 0)}
                    tone="rose"
                    testid="kpi-pending"
                />
                <Kpi
                    label="Top district"
                    icon={<MapPin size={18} />}
                    value={loading ? null : topDistrict?.district_name || "—"}
                    subValue={topDistrict ? formatInr(topDistrict.total_paise) : null}
                    tone="sky"
                    testid="kpi-top-district"
                />
                <Kpi
                    label="Top package"
                    icon={<Layers size={18} />}
                    value={loading ? null : topPackage?.package_name || "—"}
                    subValue={topPackage ? `${topPackage.subscriptions_sold} sold` : null}
                    tone="violet"
                    testid="kpi-top-package"
                />
            </section>

            <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="col-span-2 rounded-2xl border border-neutral-200 p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <h2 className="text-sm font-bold text-neutral-800">6-month trend</h2>
                            <p className="text-xs text-neutral-500">Total revenue + subscription revenue</p>
                        </div>
                    </div>
                    <div className="h-64 w-full">
                        {loading || !summary ? (
                            <Skeleton className="h-full w-full rounded-xl" />
                        ) : (
                            <TrendChart data={summary.series} />
                        )}
                    </div>
                </Card>
                <Card className="rounded-2xl border border-neutral-200 p-4">
                    <h2 className="text-sm font-bold text-neutral-800">Breakdown by type</h2>
                    <p className="text-xs text-neutral-500">Current month</p>
                    <div className="h-64 w-full">
                        {loading || !monthly ? (
                            <Skeleton className="h-full w-full rounded-xl" />
                        ) : (
                            <ByTypePie data={monthly.by_type} />
                        )}
                    </div>
                </Card>
            </section>

            <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card className="rounded-2xl border border-neutral-200 p-4">
                    <h2 className="text-sm font-bold text-neutral-800">Top 5 districts</h2>
                    <div className="h-72 w-full">
                        {loading || !monthly ? (
                            <Skeleton className="h-full w-full rounded-xl" />
                        ) : (
                            <TopDistrictsBar data={monthly.by_district?.slice(0, 5) || []} />
                        )}
                    </div>
                </Card>
                <Card className="rounded-2xl border border-neutral-200 p-4">
                    <h2 className="text-sm font-bold text-neutral-800">Top 5 packages</h2>
                    <div className="mt-3">
                        {loading || !monthly ? (
                            <Skeleton className="h-40 w-full rounded-xl" />
                        ) : monthly.by_package?.length ? (
                            <table className="w-full text-sm" data-testid="top-packages-table">
                                <thead>
                                    <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500">
                                        <th className="py-2">Package</th>
                                        <th className="py-2 text-right">Sold</th>
                                        <th className="py-2 text-right">Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {monthly.by_package.slice(0, 5).map((p) => (
                                        <tr key={p.package_slug} className="border-b border-neutral-100">
                                            <td className="py-2 font-medium text-neutral-800">{p.package_name}</td>
                                            <td className="py-2 text-right">{p.subscriptions_sold}</td>
                                            <td className="py-2 text-right font-mono text-neutral-800">
                                                {formatInr(p.total_paise)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="py-6 text-center text-sm text-neutral-500">
                                No package sales this month.
                            </div>
                        )}
                    </div>
                </Card>
            </section>
        </div>
    );
}

function Kpi({ label, value, subValue, icon, tone = "orange", testid }) {
    const tones = {
        orange: "from-orange-500 to-orange-400",
        emerald: "from-emerald-500 to-teal-400",
        amber: "from-amber-500 to-yellow-400",
        rose: "from-rose-500 to-pink-500",
        sky: "from-sky-500 to-cyan-400",
        violet: "from-violet-500 to-fuchsia-400",
    };
    return (
        <Card className="rounded-2xl border border-neutral-200 p-4" data-testid={testid}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br text-white ${tones[tone]}`}>
                {icon}
            </div>
            <div className="mt-3 text-[11px] font-medium uppercase tracking-wide text-neutral-500">{label}</div>
            {value === null ? (
                <Skeleton className="mt-1 h-7 w-24 rounded" />
            ) : (
                <div className="mt-0.5 text-2xl font-extrabold text-neutral-900" data-testid={`${testid}-value`}>{value}</div>
            )}
            {subValue && <div className="mt-0.5 text-xs text-neutral-500">{subValue}</div>}
        </Card>
    );
}

function TrendChart({ data }) {
    const rows = data.map((r) => ({
        label: monthNameOf(r.year, r.month),
        total: r.total_paise / 100,
        subscription: (r.by_type?.SUBSCRIPTION || 0) / 100,
    }));
    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    tickFormatter={(v) => compactInr(v * 100)}
                />
                <Tooltip
                    formatter={(v) => formatInr(Number(v) * 100)}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="total" stroke="#FF7A1A" strokeWidth={2.5} dot={{ r: 3 }} name="Total" />
                <Line type="monotone" dataKey="subscription" stroke="#FFC300" strokeWidth={2} dot={{ r: 3 }} name="Subscriptions" />
            </LineChart>
        </ResponsiveContainer>
    );
}

function ByTypePie({ data }) {
    const rows = TX_TYPES.map((t) => ({ name: t.label, value: (data[t.value] || 0) / 100, key: t.value })).filter(
        (r) => r.value > 0,
    );
    if (!rows.length) return <div className="flex h-full items-center justify-center text-xs text-neutral-500">No data.</div>;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart>
                <Pie data={rows} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {rows.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip
                    formatter={(v) => formatInr(Number(v) * 100)}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
        </ResponsiveContainer>
    );
}

function TopDistrictsBar({ data }) {
    const rows = data.map((d) => ({ name: d.district_name || d.district_slug, revenue: d.total_paise / 100 }));
    if (!rows.length) return <div className="flex h-full items-center justify-center text-xs text-neutral-500">No data.</div>;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 8, left: 32, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => compactInr(v * 100)} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#94a3b8" width={100} />
                <Tooltip
                    formatter={(v) => formatInr(Number(v) * 100)}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                />
                <Bar dataKey="revenue" fill="#FF7A1A" radius={[0, 6, 6, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}
